import React, { useState, useRef, useEffect } from 'react';
import { TrendingUp, Building2, Users, Award, Lock, LogOut, Settings, Menu, Network, Calendar, ClipboardList, Sparkles, BookOpen, Shield } from 'lucide-react';

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return parts.length > 1 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : name.slice(0, 2).toUpperCase();
};

const NAV_CONFIG = {
  super_admin: [
    { id: 'dashboard', label: 'Dashboard', icon: TrendingUp },
    { id: 'org', label: 'Org Settings', icon: Network },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'cycles', label: 'Cycles', icon: Calendar },
    { id: 'reviews', label: 'Reviews', icon: ClipboardList },
    { id: 'plans', label: 'AI Plans', icon: Sparkles },
    { id: 'resources', label: 'Resources', icon: BookOpen },
    { id: 'badges', label: 'Badges', icon: Award },
    { id: 'audit', label: 'Audit Log', icon: Shield },
  ],
  admin: [
    { id: 'dashboard', label: 'Dashboard', icon: TrendingUp },
    { id: 'org', label: 'Org Settings', icon: Network },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'cycles', label: 'Cycles', icon: Calendar },
    { id: 'reviews', label: 'Reviews', icon: ClipboardList },
    { id: 'plans', label: 'AI Plans', icon: Sparkles },
    { id: 'resources', label: 'Resources', icon: BookOpen },
    { id: 'badges', label: 'Badges', icon: Award },
    { id: 'audit', label: 'Audit Log', icon: Shield },
  ],
  dept_head: [
    { id: 'team', label: 'Department', icon: Building2 },
    { id: 'reviews', label: 'Reviews', icon: ClipboardList },
    { id: 'performance', label: 'Performance', icon: Award },
  ],
  manager: [
    { id: 'team', label: 'My Team', icon: Users },
    { id: 'reviews', label: 'Reviews', icon: ClipboardList },
    { id: 'performance', label: 'Performance', icon: Award },
  ],
  employee: [
    { id: 'dashboard', label: 'Dashboard', icon: TrendingUp },
    { id: 'reviews', label: 'My Reviews', icon: ClipboardList },
    { id: 'resources', label: 'Resources', icon: BookOpen },
    { id: 'recognition', label: 'Recognition', icon: Award },
  ],
};

const Sidebar = ({ user, activeTab, onTabChange, onLogout, onChangePassword }) => {
  const [showProfile, setShowProfile] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const profileRef = useRef(null);

  const navItems = NAV_CONFIG[user.role] || [];

  useEffect(() => {
    const handleClick = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setShowProfile(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <>
      {/* ========== DESKTOP SIDEBAR ========== */}
      <div className="hidden lg:flex bg-white w-[3.625rem] h-[calc(100vh-1.5rem)] rounded-3xl overflow-visible p-3 m-3 flex-col items-center gap-2 relative z-50">
        {/* Logo */}
        <div className="flex items-center justify-center mb-4">
          <img src="/logo.png" alt="Paywize" className="w-7 h-7 rounded-lg" />
        </div>

        {/* Nav Items */}
        <div className="flex flex-col gap-3 flex-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            return (
              <div key={item.id} className="relative group">
                <button
                  onClick={() => onTabChange(item.id)}
                  aria-label={item.label}
                  className={`h-[2.125rem] w-[2.125rem] flex items-center justify-center rounded-full transition-all duration-200 ${
                    isActive
                      ? 'bg-pw-tertiary text-white'
                      : 'text-pw-muted hover:bg-pw-surface-hover hover:text-pw-text'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                </button>
                {/* Tooltip */}
                <div className="absolute left-full top-1/2 -translate-y-1/2 ml-5 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-[9999]">
                  <div className="relative">
                    <div className="absolute left-[-6px] top-1/2 -translate-y-1/2 w-0 h-0 border-t-[8px] border-t-transparent border-b-[8px] border-b-transparent border-r-[8px] border-r-pw-tertiary" />
                    <div className="bg-pw-tertiary text-white text-[0.8125rem] font-bold px-3 py-2 rounded-xl whitespace-nowrap">
                      {item.label}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Profile */}
        <div ref={profileRef} className="relative">
          <button
            onClick={() => setShowProfile(!showProfile)}
            aria-label="Profile menu"
            className="h-[2.125rem] w-[2.125rem] rounded-full bg-pw-primary text-white flex items-center justify-center text-xs font-bold hover:bg-pw-primary-hover transition-colors"
          >
            {getInitials(user.name)}
          </button>

          {showProfile && (
            <div className="absolute bottom-full left-full mb-2 ml-2 z-[9999]">
              <div className="bg-white rounded-xl shadow-xl border border-pw-border-variant overflow-hidden w-52 animate-scale-in">
                <div className="px-4 py-3 border-b border-pw-border-variant">
                  <p className="text-sm font-bold text-pw-text truncate">{user.name}</p>
                  <p className="text-xs text-pw-muted truncate">{user.email}</p>
                  <span className="inline-block mt-1 text-[0.625rem] font-semibold text-pw-primary bg-pw-accent-light rounded-full px-2 py-0.5 uppercase">
                    {user.role}
                  </span>
                </div>
                <button
                  onClick={() => { setShowProfile(false); onChangePassword(); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-pw-surface-hover text-sm font-semibold text-pw-text transition-colors"
                >
                  <Lock className="w-4 h-4 text-pw-text-variant" />
                  Change Password
                </button>
                <div className="border-t border-pw-border-variant" />
                <button
                  onClick={() => { setShowProfile(false); onLogout(); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-pw-on-error text-sm font-semibold text-pw-error transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ========== MOBILE BOTTOM TAB BAR ========== */}
      <nav
        className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl border-t border-pw-border-variant/80"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="flex items-stretch h-16">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => { onTabChange(item.id); setShowMore(false); }}
                className="flex flex-col items-center justify-center gap-0.5 flex-1 active:opacity-60 transition-opacity"
              >
                <div className={isActive ? 'text-pw-primary' : 'text-pw-hint'}>
                  <Icon className="w-[22px] h-[22px]" />
                </div>
                <span className={`text-[0.625rem] font-semibold leading-none ${isActive ? 'text-pw-primary' : 'text-pw-hint'}`}>
                  {item.label}
                </span>
              </button>
            );
          })}

          {/* More */}
          <button
            onClick={() => setShowMore(!showMore)}
            className="flex flex-col items-center justify-center gap-0.5 flex-1 active:opacity-60 transition-opacity"
          >
            <div className={showMore ? 'text-pw-primary' : 'text-pw-hint'}>
              <Menu className="w-[22px] h-[22px]" />
            </div>
            <span className={`text-[0.625rem] font-semibold leading-none ${showMore ? 'text-pw-primary' : 'text-pw-hint'}`}>
              More
            </span>
          </button>
        </div>
      </nav>

      {/* ========== MOBILE "MORE" BOTTOM SHEET ========== */}
      {showMore && (
        <>
          <div
            className="lg:hidden fixed inset-0 z-[55] bg-black/30 backdrop-blur-sm animate-fade-in"
            onClick={() => setShowMore(false)}
          />
          <div
            className="lg:hidden fixed bottom-0 left-0 right-0 z-[56] bg-white rounded-t-3xl animate-slide-up-sheet max-h-[80vh] overflow-y-auto"
            style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
          >
            {/* Drag handle */}
            <div className="flex justify-center pt-3 pb-4 sticky top-0 bg-white rounded-t-3xl z-10">
              <div className="w-10 h-1 bg-pw-border-variant rounded-full" />
            </div>

            {/* Profile card */}
            <div className="px-5 pb-4">
              <div className="flex items-center gap-3 bg-pw-surface rounded-2xl p-3.5">
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-pw-primary to-pw-sidebar text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                  {getInitials(user.name)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-pw-text truncate">{user.name}</p>
                  <p className="text-xs text-pw-muted truncate">{user.email}</p>
                </div>
                <span className="text-[0.625rem] font-semibold text-pw-primary bg-pw-accent-light rounded-full px-2 py-0.5 uppercase flex-shrink-0">
                  {user.role}
                </span>
              </div>
            </div>

            {/* General section */}
            <div className="px-5 pb-4">
              <p className="text-[0.625rem] font-bold text-pw-hint uppercase tracking-wider px-1 mb-2">General</p>
              <div className="bg-pw-surface rounded-2xl overflow-hidden divide-y divide-pw-border-variant/50">
                <button
                  onClick={() => { setShowMore(false); onChangePassword(); }}
                  className="w-full flex items-center gap-3.5 px-4 py-3.5 active:bg-pw-surface-hover transition-colors"
                >
                  <div className="w-8 h-8 rounded-xl bg-white flex items-center justify-center text-pw-text-variant flex-shrink-0 shadow-sm">
                    <Lock className="w-[18px] h-[18px]" />
                  </div>
                  <span className="text-sm font-semibold text-pw-text flex-1 text-left">Change Password</span>
                  <svg className="text-pw-border" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
                <button
                  onClick={() => { setShowMore(false); onChangePassword(); }}
                  className="w-full flex items-center gap-3.5 px-4 py-3.5 active:bg-pw-surface-hover transition-colors hidden"
                >
                  <div className="w-8 h-8 rounded-xl bg-white flex items-center justify-center text-pw-text-variant flex-shrink-0 shadow-sm">
                    <Settings className="w-[18px] h-[18px]" />
                  </div>
                  <span className="text-sm font-semibold text-pw-text flex-1 text-left">Settings</span>
                </button>
              </div>
            </div>

            {/* Logout */}
            <div className="px-5 pb-6">
              <button
                onClick={() => { setShowMore(false); onLogout(); }}
                className="w-full flex items-center justify-center gap-2 bg-pw-on-error rounded-2xl py-3.5 text-sm font-bold text-pw-error active:bg-red-100 transition-colors"
              >
                <LogOut className="w-[18px] h-[18px]" />
                Logout
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Sidebar;
